import React, { useCallback, useState } from 'react';
import { Avatar, Typography, Tooltip, Tag, Space, Divider, Button, Modal, Dropdown, MenuProps, Popover } from 'antd';
import { UserOutlined, MessageOutlined, DeleteOutlined, MoreOutlined, EditOutlined, EnterOutlined, SmileOutlined, PlusOutlined } from '@ant-design/icons';
import { Message } from '../../types/chat';
import FormattedContent from './FormattedContent';
import FileAttachment from './FileAttachment';
import ServiceMessage from './ServiceMessage';
import { 
  isValidFileInfo, 
  formatMessageTime, 
  formatFullDate, 
  getMessageContent,
  hasBeenRedacted,
  isServiceMessage,
  processFormattedContent,
  hasBeenEdited
} from './utils';
import '../styles/MessageGroup.css';
import { notificationService } from '../../services/NotificationService';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface MessageGroupProps {
  messages: Message[];
  allMessages?: Message[]; // All messages for finding replies
  onDeleteMessage?: (roomId: string, eventId: string) => void;
  onReplyMessage?: (message: Message) => void;
  onEditMessage?: (message: Message) => void;
  webSocket?: WebSocket | null;
  highlightedMessageId?: string | null;
}

// Emoji picker component
const EmojiPicker: React.FC<{ onEmojiSelect: (emoji: string) => void }> = ({ onEmojiSelect }) => {
  const popularEmojis = [
    '👍', '👎', '❤️', '😀', '😂', '😍', '😢', '😮', '😡', '🎉',
    '🔥', '⭐', '✅', '❌', '👏', '🙏', '💯', '🚀', '💡', '🎯'
  ];
  const { t } = useTranslation();

  return (
    <div className="emoji-picker">
      <div className="emoji-grid">
        {popularEmojis.map((emoji) => (
          <button
            key={emoji}
            className="emoji-button"
            onClick={() => onEmojiSelect(emoji)}
            title={`${t('emojiPicker.addReaction')} ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

// Component to render reactions
const MessageReactions: React.FC<{ 
  message: Message, 
  allMessages: Message[], 
  webSocket?: WebSocket | null,
  currentUser?: string 
}> = ({ message, allMessages, webSocket, currentUser }) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { t } = useTranslation();
  // Send reaction via WebSocket
  const sendReaction = useCallback((emoji: string) => {
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN || !message.room_id || !message.event_id) {
      console.error('WebSocket не подключен или недостает данных для отправки реакции');
      return;
    }

    const reactionMessage = {
      type: 'send_reaction',
      roomId: message.room_id,
      eventId: message.event_id,
      emoji: emoji
    };

    webSocket.send(JSON.stringify(reactionMessage));
    setShowEmojiPicker(false);
  }, [webSocket, message.room_id, message.event_id]);

  // Remove reaction via WebSocket
  const removeReaction = useCallback((reactionEventId: string) => {
    if (!webSocket || webSocket.readyState !== WebSocket.OPEN || !message.room_id) {
      console.error('WebSocket не подключен или недостает данных для удаления реакции');
      return;
    }

    const deleteMessage = {
      type: 'delete_message',
      roomId: message.room_id,
      eventId: reactionEventId
    };

    webSocket.send(JSON.stringify(deleteMessage));
  }, [webSocket, message.room_id]);

  // Handle reaction click (add/remove)
  const handleReactionClick = useCallback((emoji: string, hasMyReaction: boolean, myReactionId?: string) => {
    if (hasMyReaction && myReactionId) {
      // Remove my reaction
      removeReaction(myReactionId);
    } else {
      // Add new reaction
      sendReaction(emoji);
    }
  }, [sendReaction, removeReaction]);

  // Handle emoji selection from picker
  const handleEmojiSelect = useCallback((emoji: string) => {
    sendReaction(emoji);
  }, [sendReaction]);

  if (!message.event_id) return null;

  // Find all reactions associated with this message
  const reactions = allMessages
    .filter(msg => {
      if (msg.type !== 'm.reaction') return false;
      
      // Check if content is an object with m_relates_to
      const contentObj = typeof msg.content === 'object' ? msg.content : null;
      if (!contentObj) return false;
      
      // Check if this reaction has been redacted
      if (hasBeenRedacted(msg, allMessages)) return false;
      
      // Check if this reaction relates to current message
      return contentObj.m_relates_to === message.event_id && !!contentObj.key;
    })
    .map(reaction => {
      const contentObj = typeof reaction.content === 'object' ? reaction.content : null;
      return {
        key: contentObj?.key || '',
        sender: reaction.sender,
        event_id: reaction.event_id,
        me: reaction.me
      };
    });

  // Group reactions by emoji
  const groupedReactions: {[key: string]: {count: number, senders: string[], reactionIds: string[], hasMyReaction: boolean, myReactionId?: string}} = {};
  
  reactions.forEach(reaction => {
    if (!reaction.key) return;
    
    if (!groupedReactions[reaction.key]) {
      groupedReactions[reaction.key] = { count: 0, senders: [], reactionIds: [], hasMyReaction: false };
    }
    groupedReactions[reaction.key].count += 1;
    groupedReactions[reaction.key].senders.push(reaction.sender);
    if (reaction.event_id) {
      groupedReactions[reaction.key].reactionIds.push(reaction.event_id);
    }
    
    // Check if current user has this reaction
    if (reaction.me) {
      groupedReactions[reaction.key].hasMyReaction = true;
      groupedReactions[reaction.key].myReactionId = reaction.event_id;
    }
  });

  return (
    <div className="message-reactions">
      <Space size={4} wrap>
        {/* Existing reactions */}
        {Object.entries(groupedReactions).map(([emoji, data]) => {
          // Проверяем, есть ли среди реакций новые
          const hasNewReaction = data.reactionIds.some(reactionId => 
            notificationService.isReactionNew(reactionId)
          );
          
          return (
            <Tooltip 
              key={emoji} 
              title={
                data.hasMyReaction 
                  ? `${data.senders.filter(s => s !== currentUser).join(', ')} (${t('emojiPicker.removeReaction')})`
                  : `${data.senders.join(', ')} (${t('emojiPicker.addReaction')})`
              }
            >
              <Tag 
                className={`reaction-tag${hasNewReaction ? ' new-reaction' : ''}${data.hasMyReaction ? ' my-reaction' : ''}`}
                onClick={() => handleReactionClick(emoji, data.hasMyReaction, data.myReactionId)}
                style={{ cursor: 'pointer' }}
              >
                {emoji} {data.count > 1 ? data.count : ''}
              </Tag>
            </Tooltip>
          );
        })}
        
        {/* Add reaction button */}
        <Popover
          content={<EmojiPicker onEmojiSelect={handleEmojiSelect} />}
          title={t('emojiPicker.title')}
          trigger="click"
          open={showEmojiPicker}
          onOpenChange={setShowEmojiPicker}
          placement="topLeft"
          overlayClassName="emoji-picker-popover"
        >
          <Button 
            type="text" 
            size="small" 
            icon={<SmileOutlined />}
            className="add-reaction-btn"
            title={t('emojiPicker.addReaction')}
          />
        </Popover>
      </Space>
    </div>
  );
};

// Component for message replies
const ReplyPreview: React.FC<{ replyTo: any, allMessages: Message[] }> = ({ replyTo, allMessages }) => {
  // Always define hooks at the top level, even if we return early
  const { t } = useTranslation();
  const handleReplyClick = useCallback(() => {
    if (!replyTo || !replyTo.event_id) return;
    
    // Find the message being replied to
    const repliedMessage = allMessages.find(msg => msg.event_id === replyTo.event_id);
    if (!repliedMessage) return; // Do nothing if message not found
    
    // Find the element by event_id
    const messageElement = document.getElementById(`message-${replyTo.event_id}`);
    if (messageElement) {
      // Scroll to the message with improved logic
      const messagesContainer = document.querySelector('.messages-container');
      if (messagesContainer) {
        const containerRect = messagesContainer.getBoundingClientRect();
        const messageRect = messageElement.getBoundingClientRect();
        
        // Если сообщение находится в нижней части контейнера,
        // используем 'end' вместо 'center' чтобы не нарушать верстку
        const isNearBottom = messageRect.bottom > containerRect.bottom - 100;
        
        messageElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: isNearBottom ? 'end' : 'center',
          inline: 'nearest'
        });
      } else {
        // Fallback к старому поведению
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Highlight the message temporarily
      messageElement.classList.add('message-highlight');
      setTimeout(() => {
        messageElement.classList.remove('message-highlight');
      }, 2000);
    }
  }, [replyTo, allMessages]);
  
  // Early return if no reply data
  if (!replyTo || !replyTo.event_id) return null;
  
  // Find the message being replied to
  const repliedMessage = allMessages.find(msg => msg.event_id === replyTo.event_id);
  
  // ВАЖНО: Всегда используем актуальные данные из allMessages, если сообщение найдено
  // Если не найдено, используем данные из preview как fallback
  const sender = repliedMessage?.sender || replyTo.sender;
  const content = repliedMessage ? getMessageContent(repliedMessage) : replyTo.content;
  const isEdited = repliedMessage ? hasBeenEdited(repliedMessage) : (replyTo.edited || false);
  
  return (
    <div className="reply-preview" onClick={handleReplyClick} style={{ cursor: repliedMessage ? 'pointer' : 'default' }}>
      <div className="reply-preview-indicator"></div>
      <div className="reply-preview-content">
        {!replyTo.me && (
          <Text type="secondary" strong>
            {sender}
            {isEdited && (
              <span style={{ marginLeft: '4px', fontSize: '0.8em', fontWeight: 'normal' }}>
                ({t('chat.edited')})
              </span>
            )}
          </Text>
        )}
        <div 
          className="reply-preview-text"
          dangerouslySetInnerHTML={{ 
            __html: processFormattedContent(content) 
          }}
        />
      </div>
    </div>
  );
};

// Component for message actions menu
const MessageActionsMenu: React.FC<{ 
  message: Message, 
  onDelete: (roomId: string, eventId: string) => void,
  allMessages: Message[],
  onReply: (message: Message) => void,
  onEdit?: (message: Message) => void,
  onAddReaction?: () => void
}> = ({ message, onDelete, allMessages, onReply, onEdit, onAddReaction }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const { t } = useTranslation();
  const handleReply = useCallback(() => {
    onReply(message);
  }, [onReply, message]);

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(message);
    }
  }, [onEdit, message]);

  const handleAddReaction = useCallback(() => {
    if (onAddReaction) {
      onAddReaction();
    }
  }, [onAddReaction]);

  const handleDelete = useCallback(() => {
    setIsDeleteModalVisible(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (message.room_id && message.event_id) {
      onDelete(message.room_id, message.event_id);
    }
    setIsDeleteModalVisible(false);
  }, [message.room_id, message.event_id, onDelete]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalVisible(false);
  }, []);

  // Don't show menu for already redacted messages
  if (hasBeenRedacted(message, allMessages)) {
    return null;
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'reply',
      label: t('chat.reply'),
      icon: <EnterOutlined />,
      onClick: handleReply,
    },
    {
      key: 'reaction',
      label: t('chat.addReaction'),
      icon: <SmileOutlined />,
      onClick: handleAddReaction,
    },
    // Only show edit and delete for own messages
    ...(message.me ? [
      {
        type: 'divider' as const,
      },
      {
        key: 'edit',
        label: t('chat.edit'),
        icon: <EditOutlined />,
        onClick: handleEdit,
      },
      {
        key: 'delete',
        label: t('chat.delete'),
        icon: <DeleteOutlined />,
        onClick: handleDelete,
        danger: true,
      },
    ] : []),
  ];

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomLeft"
        overlayClassName="message-actions-dropdown"
        getPopupContainer={() => document.body}
        overlayStyle={{ zIndex: 99999 }}
      >
        <Button 
          type="text" 
          size="small" 
          icon={<MoreOutlined />} 
          className="message-actions-btn"
          title={t('chat.messageActions')}
        />
      </Dropdown>
      
      <Modal
        title={t('chat.deleteMessage')}
        open={isDeleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText={t('chat.delete')}
        cancelText={t('chat.cancel')}
        okButtonProps={{ danger: true }}
        centered
      >
        <p>{t('chat.deleteMessageConfirm')}</p>
      </Modal>
    </>
  );
};

const MessageGroup: React.FC<MessageGroupProps> = ({ messages, allMessages = [], onDeleteMessage, onReplyMessage, onEditMessage, webSocket, highlightedMessageId }) => {
  const [showEmojiPickerForMessage, setShowEmojiPickerForMessage] = useState<{[key: string]: boolean}>({});
  const { t } = useTranslation();
  // Function to toggle emoji picker for specific message
  const toggleEmojiPicker = useCallback((messageIndex: number) => {
    setShowEmojiPickerForMessage(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  }, []);

  // Function to send reaction for specific message
  const sendReactionForMessage = useCallback((msg: Message, emoji: string, messageIndex: number) => {
    if (webSocket && webSocket.readyState === WebSocket.OPEN && msg.room_id && msg.event_id) {
      const reactionMessage = {
        type: 'send_reaction',
        roomId: msg.room_id,
        eventId: msg.event_id,
        emoji: emoji
      };
      webSocket.send(JSON.stringify(reactionMessage));
    }
    setShowEmojiPickerForMessage(prev => ({
      ...prev,
      [messageIndex]: false
    }));
  }, [webSocket]);

  // Get current user from JWT token
  const getCurrentUser = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return undefined;
      
      // Decode JWT payload (simple base64 decode, no verification needed for username)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      return payload.username;
    } catch (error) {
      console.error('Error decoding token:', error);
      return undefined;
    }
  }, []);
  
  const currentUser = getCurrentUser();
  
  if (!messages || messages.length === 0) return null;
  
  // Use the first message for sender info and avatar
  const firstMessage = messages[0];
  
  // For service messages, render a simple centered message without avatar
  if (isServiceMessage(firstMessage)) {
    return (
      <div className="service-message-container">
        {messages.map((msg, index) => (
          <ServiceMessage key={index} message={msg} />
        ))}
      </div>
    );
  }
  
  return (
    <div className="message message-left">
      <Avatar 
        style={{ backgroundColor: firstMessage.me ? '#1677ff' : '#f56a00' }}
        icon={<UserOutlined />}
      />
      <div className="message-content">
        {!firstMessage.me && (
          <div className="message-sender">
            <Text strong>{firstMessage.sender}</Text>
          </div>
        )}
        
        {/* Render each message in the group */}
        <div className="message-bubbles-container">
          {messages.map((msg, index) => {
            // Check if msg.content is an object with reply_to
            const contentObj = typeof msg.content === 'object' ? msg.content : null;
            const replyTo = contentObj?.reply_to;
            
            // Check if message has been redacted
            const isRedacted = hasBeenRedacted(msg, allMessages);
            
            return (
              <div 
                key={index} 
                className={`message-bubble ${msg.me ? 'message-bubble-me' : 'message-bubble-other'} ${index !== messages.length - 1 ? 'message-bubble-consecutive' : ''} ${isRedacted ? 'message-redacted' : ''} ${highlightedMessageId === msg.event_id ? 'message-highlighted' : ''}`}
                id={`message-${msg.event_id}`}
                data-event-id={msg.event_id}
              >
                {/* Actions menu in top right corner */}
                {onDeleteMessage && !isRedacted && (
                  <div className="message-actions-container">
                    <MessageActionsMenu 
                      message={msg} 
                      onDelete={onDeleteMessage} 
                      allMessages={allMessages}
                      onReply={onReplyMessage || (() => {})}
                      onEdit={onEditMessage}
                      onAddReaction={() => toggleEmojiPicker(index)}
                    />
                    
                    {/* Emoji picker for actions menu */}
                    <Popover
                      content={
                        <EmojiPicker 
                          onEmojiSelect={(emoji) => sendReactionForMessage(msg, emoji, index)}
                        />
                      }
                      title={t('emojiPicker.title')}
                      trigger="click"
                      open={showEmojiPickerForMessage[index] || false}
                      onOpenChange={(open) => setShowEmojiPickerForMessage(prev => ({
                        ...prev,
                        [index]: open
                      }))}
                      placement="topLeft"
                      overlayClassName="emoji-picker-popover"
                    />
                  </div>
                )}

                {/* Reply preview if this message is a reply */}
                {replyTo && !isRedacted && (
                  <ReplyPreview replyTo={replyTo} allMessages={allMessages} />
                )}
                
                {isRedacted ? (
                  <div className="redacted-message">
                    <DeleteOutlined style={{ marginRight: 8 }} />
                    <Text type="secondary" italic>{t('chat.deletedMessage')}</Text>
                  </div>
                ) : (
                  <>
                    {isValidFileInfo(msg.fileInfo) && (
                      <FileAttachment fileInfo={msg.fileInfo} />
                    )}
                    {!isValidFileInfo(msg.fileInfo) && (
                      <FormattedContent message={msg} />
                    )}
                  </>
                )}
                <div className="message-footer">
                  {/* Reactions for this message */}
                  <MessageReactions 
                    message={msg} 
                    allMessages={allMessages} 
                    webSocket={webSocket}
                    currentUser={currentUser}
                  />
                  
                  <div className="message-time">
                    <Tooltip title={formatFullDate(msg.created)}>
                      {formatMessageTime(msg.created)}
                    </Tooltip>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MessageGroup; 